import { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useMatch, useParams } from 'react-router-dom';
import CookieBanner from './components/CookieBanner';
import Header from './components/Header';
import LandingHeader from './components/LandingHeader';
import Footer from './components/Footer';
import DynamicPage from './pages/DynamicPage';
import LandingPage from './pages/LandingPage';
import GalleryPage from './pages/GalleryPage';
import StatusPage from './pages/StatusPage';
import { api, type ApiChrome, type ApiPageSummary } from './lib/api';
import { applyAnalyticsConsent, trackPageView } from './lib/analytics';
import { applyTrackingCodes, clearTrackingCodes, ga4Of } from './lib/tags';
import { config } from './lib/config';
import { onConsentChange, readConsent, type ConsentState } from './lib/consent';
import { onHeadApplied } from './lib/head';
import { isPreviewing } from './lib/preview';
import ScrollReset from './lib/scroll';
import PreviewBanner from './components/PreviewBanner';
import { makeT } from './lib/i18n';
import { FALLBACK_REGIONS, isKnownLocale, readRegionOverride, resolveRegion, type Region } from './lib/regions';
import { SiteContext } from './lib/site-context';

/** Loads the region list from the CMS, falling back to the static table if it fails. */
function useRegions() {
  const [regions, setRegions] = useState<Region[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.regions()
      .then((res) => { if (!cancelled) setRegions(res.length ? res : FALLBACK_REGIONS); })
      .catch(() => { if (!cancelled) setRegions(FALLBACK_REGIONS); });
    return () => { cancelled = true; };
  }, []);

  return regions;
}

/**
 * The market's tags, and only with permission.
 *
 * Three jobs: keep this region's tracking codes in step with the visitor's decision,
 * keep GA4 in step with it too, and send one page view per page.
 *
 * GA4 is handled apart from the rest, and not because it is Google. A pasted tag snippet
 * in a single-page app measures exactly one page view - the one it was loaded for - and
 * then never hears about a route change again, so the tag that is meant to count the site
 * would count the landing page. `analytics.ts` runs it properly instead, from the
 * measurement id. Everything else is a snippet, injected as written.
 *
 * The page view waits for the head to be written rather than for the URL to change,
 * because until the CMS answers the document still carries the previous page's title -
 * see lib/head.ts.
 */
function Analytics({ region, consentVersion }: { region: Region; consentVersion: string | null }) {
  const [consent, setConsent] = useState<ConsentState | null>(() => readConsent());
  const { pathname, search } = useLocation();
  // The URL a view has actually been sent for. Not "the URL we tried": the attempt made
  // before the visitor accepted was dropped, and that page still deserves counting once
  // they do - otherwise the page every visitor lands on is the one page never recorded.
  const sentFor = useRef<string | null>(null);

  useEffect(() => onConsentChange(setConsent), []);

  // A decision only counts for the wording it was given for. If the CMS text has been
  // rewritten since, the visitor has not consented to the current text and nothing loads
  // until they answer again.
  const current = Boolean(consent && consentVersion && consent.version === consentVersion);
  const granted = useMemo(
    () => ({
      analytics: current && consent?.analytics === true,
      marketing: current && consent?.marketing === true
    }),
    [current, consent?.analytics, consent?.marketing]
  );

  const codes = useMemo(() => region.trackingCodes ?? [], [region.trackingCodes]);
  const ga4 = ga4Of(codes);
  // GA4 carries a category like every other tag, even though in practice it is analytics.
  const ga4Allowed = Boolean(ga4 && granted[ga4.consentCategory]);
  const allowed = ga4Allowed;

  const url = `${pathname}${search}`;

  useEffect(() => {
    applyAnalyticsConsent(ga4?.measurementId ?? null, ga4Allowed);
  }, [ga4?.measurementId, ga4Allowed]);

  useEffect(() => {
    applyTrackingCodes(codes, granted);
  }, [codes, granted]);

  // Leaving the site behind: a tab that navigates away should not leave another region's
  // tags on the page if the region ever changes under us.
  useEffect(() => () => clearTrackingCodes(), [region.code]);

  const send = (title: string) => {
    if (sentFor.current === url) return;
    if (trackPageView(url, title)) sentFor.current = url;
  };

  // A view is sent when the head is written, not when the URL changes: until the CMS
  // answers, the document still carries the previous page's title.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onHeadApplied(({ title }) => send(title)), [url]);

  // And once more the moment permission arrives, for the page already on screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (allowed) send(document.title); }, [allowed]);

  return null;
}

function LocaleShell({ region, regions, pages, pagesStatus }: {
  region: Region; regions: Region[]; pages: ApiPageSummary[];
  pagesStatus: 'loading' | 'ready' | 'failed';
}) {
  const { locale = region.defaultLocale } = useParams();
  const [chrome, setChrome] = useState<ApiChrome>({ navigation: null, footer: null, consent: null });
  // Read from the location rather than passed down: the shell draws the chrome and the
  // routes below draw the page, and only the location knows which of them is in front.
  const isLanding = useMatch('/:locale/lp/:slug') !== null;

  // The menu and the footer are content, so they are fetched per region and language.
  // Both live in one request: every page needs both, and two would be one too many.
  useEffect(() => {
    let cancelled = false;
    api.chrome(region.code, locale)
      .then((res) => { if (!cancelled) setChrome(res); })
      .catch(() => { if (!cancelled) setChrome({ navigation: null, footer: null, consent: null }); });
    return () => { cancelled = true; };
  }, [region.code, locale]);

  // The document language is the language of the page, not the one baked into
  // index.html. Screen readers pick a voice from it and search engines believe it, so a
  // Czech page announced as Polish is wrong in both directions.
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  // The page an editor ticked as the news index. Its slug is region- and
  // language-specific, which is exactly why it comes from the CMS.
  const newsIndex = pages.find((p) => p.locale === locale && p.isNewsIndex)?.slug;

  const value = useMemo(() => ({
    region,
    regions,
    locale,
    // No regex: `/\/+$/` on a long run of slashes backtracks, and the only case this
    // ever has to handle is an empty slug - the homepage, which is `/pl/` and not `/pl//`.
    path: (slug: string) => (slug ? `/${locale}/${slug}` : `/${locale}/`),
    // With no news index marked the article still resolves, one segment up - see
    // DynamicPage. That keeps links working while an editor is still setting up.
    articlePath: (articleSlug: string) =>
      newsIndex ? `/${locale}/${newsIndex}/${articleSlug}` : `/${locale}/${articleSlug}`,
    pages,
    pagesStatus,
    t: makeT(locale)
  }), [region, regions, locale, newsIndex, pages, pagesStatus]);

  if (!isKnownLocale(region, locale)) return <Navigate to={`/${region.defaultLocale}/`} replace />;

  return (
    <SiteContext.Provider value={value}>
      {isPreviewing() ? <PreviewBanner /> : null}
      <div className="flex min-h-screen flex-col">
        {/* First in the tab order, visible only once focused: a keyboard user should not
            have to walk the whole mega menu on every page to reach the content. */}
        <a
          href="#main"
          className="sr-only rounded-m bg-ink-strong px-6 py-3 text-body-sm text-white focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
        >
          {value.t('a11y.skipToContent')}
        </a>
        {/* A landing page wears a bar with the logo and nothing else - Figma 6440:35719,
            where the menu-shaped frames beside it are hidden. It is an offer reached from
            a link in an e-mail, and a mega menu on it is an invitation to leave the thing
            you were sent. The footer stays, so there is still a way to the company. */}
        {isLanding ? <LandingHeader /> : (
          <Header navigation={chrome.navigation} pages={pages.filter((p) => p.locale === locale)} />
        )}
        <main id="main" tabIndex={-1} className="flex-1 focus-visible:outline-none">
          <Routes>
            {/* `lp` is a reserved segment, which is the whole reason it exists: a landing
                page's slug lives in a space of its own, so an offer can be called
                `kontakt` without shadowing the contact page. */}
            <Route path="lp/:slug" element={<LandingPage />} />
            <Route path="*" element={<DynamicPage />} />
          </Routes>
        </main>
        <Footer footer={chrome.footer} consentLabel={chrome.consent?.settingsLabel ?? null} />
      </div>

      <Analytics region={region} consentVersion={chrome.consent?.version ?? null} />
      <CookieBanner consent={chrome.consent} />
    </SiteContext.Provider>
  );
}

export default function App() {
  const regions = useRegions();
  const [pages, setPages] = useState<ApiPageSummary[]>([]);
  // `[]` on its own says nothing: it is a region with no pages, a request still in flight
  // and a request that failed. `DynamicPage` has to tell them apart to know whether a
  // path it cannot find in the list is an article or simply not loaded yet.
  const [pagesStatus, setPagesStatus] = useState<'loading' | 'ready' | 'failed'>('loading');

  const region = useMemo(() => {
    if (!regions) return null;
    // `?region=` is how the switcher moves between regions on a host that is nobody's
    // domain - the dev server, a preview URL. It is ignored on a real regional domain,
    // where the domain decides and a query parameter must not be able to serve Czech
    // content from a Polish address. See `readRegionOverride`.
    const override = readRegionOverride(regions, window.location);
    return resolveRegion(regions, window.location.hostname, override || config.forceRegion);
  }, [regions]);

  useEffect(() => {
    if (!region) return;
    let cancelled = false;
    setPagesStatus('loading');
    api.pages(region.code, '')
      .then((res) => { if (!cancelled) { setPages(res); setPagesStatus('ready'); } })
      .catch(() => { if (!cancelled) { setPages([]); setPagesStatus('failed'); } });
    return () => { cancelled = true; };
  }, [region]);

  if (!region) return <StatusPage title="Loading…" body="" />;

  return (
    <BrowserRouter>
      {/* A route change has to start at the top of the new page, and put focus there.
          Inside the router because it reads the location; renders nothing. */}
      <ScrollReset />
      <Routes>
        <Route path="/" element={<Navigate to={`/${region.defaultLocale}/`} replace />} />
        {/* The design-system gallery. Unlinked and noindex; see pages/GalleryPage.tsx. */}
        <Route path="/__ui" element={<GalleryPage />} />
        <Route
          path="/:locale/*"
          element={<LocaleShell region={region} regions={regions ?? []} pages={pages} pagesStatus={pagesStatus} />}
        />
      </Routes>
    </BrowserRouter>
  );
}
